
import { Component, Inject, Vue, Watch } from 'vue-property-decorator';
import { Navigator, Service } from '@/domain/providers';
import { ContentType } from 'zr-arch';
import type { Navigation } from 'zr-arch';
import Services from '@/infrastructure/service';
import config from '@/config';
import { BANK_LIST } from '@/config/bankList';

@Component({})
export default class Input extends Vue {
    private static readonly WxTokenKey = 'expertcoll_wxToken';
    private static readonly DraftKeyPrefix = 'expertcoll_draft_';

    @Inject(Service) service!: Services;
    @Inject(Navigator) navigator!: Navigation;

    private valid = false;
    private submitting = false;
    private 考试名称 = '';
    private 考试标识 = '';
    private wxToken = '';
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private existingInfo: any = null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private examInfo: any = null;
    private showWeChatPrompt = false;
    private showSuccessPrompt = false;
    private successTitle = '提交成功';
    private confirmDialog = false;
    // 加载/恢复阶段抑制草稿保存，避免服务端数据覆盖已存草稿
    private hydrating = false;

    private form = {
        专家姓名: '',
        身份证号码: '',
        手机号码: '',
        收款银行: '',
        收款银行卡号: '',
        开户支行: '',
    };

    private bankItems = BANK_LIST;

    private photoTypes = ['身份证正面', '身份证反面', '本人照片'];
    private photoDesc: Record<string, string> = {
        '身份证正面': '（国徽面）',
        '身份证反面': '（人像面）',
        '本人照片': '（本人清晰面部照）',
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private uploading: Record<string, boolean> = {};
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private photoPreviews: Record<string, string> = {};
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private previewDialog: Record<string, boolean> = {};
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private photoPaths: Record<string, string> = {};

    get needIdCardPhotos(): boolean {
        return this.examInfo?.是否需上传身份证照片 ?? false;
    }

    get needBankCard(): boolean {
        return this.examInfo?.是否需填写银行卡 ?? true;
    }

    private rules = {
        required: () => true,
        idCard: (v: string) => /^\d{17}[\dXx]$/.test(v) || '请输入正确的18位身份证号码',
        phone: (v: string) => /^1\d{10}$/.test(v) || '请输入正确的手机号码',
        bankCard: (v: string) => /^\d{6,30}$/.test(v) || '请输入正确的银行卡号',
    };

    get readonly(): boolean {
        if (!this.existingInfo) return false;
        return ['已打款', '无效'].includes(this.existingInfo.状态);
    }

    get statusText(): string {
        return this.existingInfo?.状态 || '';
    }

    get alertType(): string {
        if (!this.existingInfo) return '';
        if (this.existingInfo.状态 === '信息有误') return 'error';
        if (this.existingInfo.状态 === '已打款') return 'success';
        if (this.existingInfo.状态 === '无效') return 'error';
        return '';
    }

    get alertMessage(): string {
        if (!this.existingInfo) return '';
        if (this.existingInfo.状态 === '信息有误') return '因收款信息有误打款失败，请修改后提交';
        if (this.existingInfo.状态 === '已打款') return '已打款';
        if (this.existingInfo.状态 === '无效') return '该信息无效';
        return '';
    }

    async mounted(): Promise<void> {
        const params = new URLSearchParams(window.location.search);
        this.考试标识 = params.get('id') || '';
        const code = params.get('code') || '';

        if (!this.考试标识) {
            this.service.提示.提示错误('缺少考试参数');
            return;
        }

        const isWeChat = navigator.userAgent.toLowerCase().includes('micromessenger');
        const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

        if (!isWeChat && !isLocalhost) {
            this.showWeChatPrompt = true;
            return;
        }

        // 微信选图可能导致页面被内核回收并整体重载，内存中的 token 会丢失；
        // 不先从 sessionStorage 恢复就会重新跳 OAuth，导致已填内容与已选照片丢失。
        this.wxToken = this.readStoredToken();

        await this.loadExamInfo();

        if (code) {
            await this.exchangeCodeForToken(code);
        }

        if (!this.wxToken) {
            if (isLocalhost) {
                await this.loadAndRestore();
                return;
            }
            if (isWeChat) {
                this.redirectToWeChatOAuth();
                return;
            }
        }

        await this.loadAndRestore();
    }

    private async loadAndRestore(): Promise<void> {
        this.hydrating = true;
        try {
            await this.loadData();
        } finally {
            this.hydrating = false;
        }
        this.restoreDraft();
    }

    private readStoredToken(): string {
        try {
            const raw = localStorage.getItem(Input.WxTokenKey);
            if (!raw) return '';
            const { token, ts } = JSON.parse(raw) as { token?: string; ts?: number };
            // 后端 token 有效期 24 小时，提前 1 小时视为过期，避免拿着失效 token 卡死
            if (!token || !ts || Date.now() - ts > 23 * 3600 * 1000) return '';
            return token;
        } catch {
            return '';
        }
    }

    private storeToken(token: string): void {
        try {
            localStorage.setItem(Input.WxTokenKey, JSON.stringify({ token, ts: Date.now() }));
        } catch {
        }
    }

    private async exchangeCodeForToken(code: string): Promise<void> {
        try {
            const result = await this.service.api.get('/ExpertColl/ExpertColl/GetToken', { code });
            if (result && result.token) {
                this.wxToken = result.token;
                this.storeToken(this.wxToken);
            }
        } catch (e) {
        }
        this.cleanUrl();
    }

    private cleanUrl(): void {
        const url = new URL(window.location.href);
        url.searchParams.delete('code');
        url.searchParams.delete('state');
        window.history.replaceState({}, document.title, url.toString());
    }

    private async loadExamInfo(): Promise<void> {
        try {
            const 考试 = await this.service.api.get('/ExpertColl/ExpertColl/GetExam', { examId: this.考试标识 });
            if (考试) {
                this.考试名称 = 考试.考试名称 || '';
                this.examInfo = 考试;
                this.updatePageTitle();
            }
        } catch (e) {
            this.service.提示.提示错误('加载考试信息失败：' + (e as Error).message);
        }
    }

    private redirectToWeChatOAuth(): void {
        const cleanReturnUrl = `${window.location.origin}${window.location.pathname}?id=${encodeURIComponent(this.考试标识)}`;
        const bridgeHost = (config as any).微信OAuth网关地址;
        if (bridgeHost) {
            const url =
                `${bridgeHost.replace(/\/$/, '')}/wechat-oauth`
                + `?returnUrl=${encodeURIComponent(cleanReturnUrl)}`
                + `&scope=snsapi_base`
                + `&state=expertcoll`;
            window.location.href = url;
            return;
        }
    }

    async loadData(): Promise<void> {
        try {
            const info = await this.service.api.get('/ExpertColl/ExpertColl/GetSubmittedInfo', {
                examId: this.考试标识,
                token: this.wxToken,
            });
            if (info) {
                this.existingInfo = info;
                this.fillForm(info);
                this.updatePageTitle();
                if (info.图片信息) {
                    this.parsePhotoInfo(info.图片信息);
                    await this.loadPhotoPreviews();
                }
                return;
            }

            const recent = await this.service.api.get('/ExpertColl/ExpertColl/GetRecentInfo', { token: this.wxToken });
            if (recent) {
                this.fillForm(recent);
                // 基于身份证号从 OSS 加载已有照片（跨考试复用）
                await this.loadPhotoPreviews();
            }
        } catch (e) {
        }
    }

    private updatePageTitle(): void {
        const title = this.existingInfo
            ? `${this.考试名称} - 专家信息登记 [${this.statusText}]`
            : `${this.考试名称} - 专家信息登记`;
        document.title = title;
    }

    @Watch('form', { deep: true })
    @Watch('photoPaths', { deep: true })
    private onDraftChanged(): void {
        this.saveDraft();
    }

    // 注意：必须用 localStorage。微信 WebView 调起相机/选图时进程可能被内核回收，
    // sessionStorage（内存态）会随之丢失，导致 token 恢复失败而重新跳 OAuth。
    private saveDraft(): void {
        if (this.hydrating || !this.考试标识 || this.readonly) return;
        try {
            localStorage.setItem(
                Input.DraftKeyPrefix + this.考试标识,
                JSON.stringify({ form: this.form, photoPaths: this.photoPaths })
            );
        } catch {
        }
    }

    private restoreDraft(): void {
        if (this.readonly) return;
        try {
            const raw = localStorage.getItem(Input.DraftKeyPrefix + this.考试标识);
            if (!raw) return;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const draft = JSON.parse(raw) as any;
            if (draft.form) this.fillForm(draft.form);
            if (draft.photoPaths) this.photoPaths = { ...draft.photoPaths };
            // 恢复已上传照片的预览（按身份证号从服务端重新拉取）
            if (this.form.身份证号码 && Object.keys(this.photoPaths).length > 0) {
                void this.loadPhotoPreviews();
            }
        } catch {
        }
    }

    private clearDraft(): void {
        try {
            localStorage.removeItem(Input.DraftKeyPrefix + this.考试标识);
        } catch {
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formatDeadline(val: any): string {
        if (!val) return '';
        const d = new Date(val);
        if (isNaN(d.getTime())) return String(val);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    fillForm(info: any): void {
        this.form.专家姓名 = info.专家姓名 || '';
        this.form.身份证号码 = info.身份证号码 || '';
        this.form.手机号码 = info.手机号码 || '';
        this.form.收款银行 = info.收款银行 || '';
        this.form.收款银行卡号 = info.收款银行卡号 || '';
        this.form.开户支行 = info.开户支行 || '';
    }

    private parsePhotoInfo(json: string): void {
        try {
            const data = JSON.parse(json);
            this.photoPaths = { ...data };
        } catch {
            this.photoPaths = {};
        }
    }

    private async loadPhotoPreviews(): Promise<void> {
        const idCard = this.form.身份证号码;
        if (!idCard) return;
        for (const photoType of this.photoTypes) {
            try {
                const result = await this.service.api.get('/ExpertColl/ExpertColl/GetPhotoUrl', {
                    idCard,
                    photoType,
                    token: this.wxToken,
                });
                if (result && result.url) {
                    const dataUrl = await this.service.文件.获取文件到dataUrl(result.url);
                    this.$set(this.photoPreviews, photoType, dataUrl);
                    // 同步保存路径，用于提交时带上
                    this.$set(this.photoPaths, photoType, result.path);
                }
            } catch {
            }
        }
    }

    triggerUpload(photoType: string): void {
        if (this.readonly) return;
        const refName = 'upload_' + photoType;
        const input = (this.$refs[refName] as any[])[0] as HTMLInputElement;
        if (input) input.click();
    }

    async fileChanged(file: File | undefined, photoType: string): Promise<void> {
        if (!file) return;
        const acceptExtensions = ['.jpg', '.jpeg', '.png'];
        if (acceptExtensions.every((x) => !file.name.endsWith(x))) {
            this.service.提示.提示错误(`只允许${acceptExtensions.join('、')}格式！`);
            return;
        }

        try {
            this.$set(this.uploading, photoType, true);

            const 压缩过的图片 = await this.service.图片.压缩图片(file, {
                最大体积MB: 0.5,
                最大宽或高px: 1920,
            });

            // 从后端获取上传 URL
            const result = await this.service.api.get('/ExpertColl/ExpertColl/GetPhotoPutUrl', {
                idCard: this.form.身份证号码,
                photoType,
                token: this.wxToken,
            });

            if (!result || !result.url) {
                this.service.提示.提示错误('获取上传地址失败');
                return;
            }

            // PUT 上传到 OSS
            await this.service.文件.上传(result.url, 压缩过的图片, ContentType.Jpeg);

            // 保存路径
            this.$set(this.photoPaths, photoType, result.path);

            // 获取 GET 预签名 URL 来加载预览（PUT 签名不能用于 GET）
            const getUrlResult = await this.service.api.get('/ExpertColl/ExpertColl/GetPhotoUrl', {
                idCard: this.form.身份证号码,
                photoType,
                token: this.wxToken,
            });
            if (getUrlResult && getUrlResult.url) {
                const dataUrl = await this.service.文件.获取文件到dataUrl(getUrlResult.url);
                this.$set(this.photoPreviews, photoType, dataUrl);
            }

            this.service.提示.提示成功(`${photoType}上传成功！`);
        } catch (e) {
            this.service.提示.提示错误(`${photoType}上传失败，请重新上传！`);
        } finally {
            this.$set(this.uploading, photoType, false);
            // 清空 input 的值以允许重复上传同一文件
            const refName = 'upload_' + photoType;
            const input = (this.$refs[refName] as any[])[0] as HTMLInputElement;
            if (input) input.value = '';
        }
    }

    openConfirm(): void {
        for (const key of Object.keys(this.form)) {
            if (!this.form[key as keyof typeof this.form]) {
                if (this.needBankCard || (key !== '收款银行' && key !== '收款银行卡号' && key !== '开户支行')) {
                    this.service.提示.提示错误('请填写完整信息');
                    return;
                }
            }
        }
        const formRef = this.$refs.form as any;
        if (!formRef.validate()) return;

        if (this.needIdCardPhotos) {
            const allUploaded = this.photoTypes.every(pt => this.photoPaths[pt]);
            if (!allUploaded) {
                this.service.提示.提示错误('请上传全部三张身份证照片');
                return;
            }
        }

        this.confirmDialog = true;
    }

    async doSubmit(): Promise<void> {
        this.submitting = true;
        try {
            const 图片信息 = this.needIdCardPhotos ? JSON.stringify(this.photoPaths) : null;
            const payload = { ...this.form, 图片信息 };
            if (this.existingInfo) {
                const result = await this.service.api.post('/ExpertColl/ExpertColl/UpdateInfo', payload, {
                    infoId: this.existingInfo.id,
                    token: this.wxToken,
                });
                this.existingInfo = result;
                this.updatePageTitle();
                this.showSuccessPrompt = true;
            } else {
                const result = await this.service.api.post('/ExpertColl/ExpertColl/SubmitInfo', payload, {
                    examId: this.考试标识,
                    token: this.wxToken,
                });
                this.existingInfo = result;
                this.updatePageTitle();
                this.showSuccessPrompt = true;
            }
            this.clearDraft();
            this.confirmDialog = false;
        } catch (e) {
        } finally {
            this.submitting = false;
        }
    }
}
